import React, { useState, useEffect } from 'react';
import { parAPI } from '../../services/api';
import './MasterDataManagement.css';

const ParManagement = () => {
  const [pars, setPars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingPar, setEditingPar] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [newPar, setNewPar] = useState({
    code: '',
    name: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    loadPars();
  }, [searchTerm, currentPage, pageSize]);

  const loadPars = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await parAPI.getPars(searchTerm);
      
      if (response.success) {
        setPars(response.data);
        setTotalCount(response.data.length);
        setTotalPages(Math.ceil(response.data.length / pageSize));
      } else {
        setError(response.message || 'Failed to load PARs');
      }
    } catch (err) {
      console.error('Load PARs error:', err);
      setError('An error occurred while loading PARs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePar = () => {
    setModalMode('create');
    setEditingPar(null);
    setNewPar({
      code: '',
      name: ''
    });
    setShowModal(true);
  };

  const handleEditPar = (par) => {
    setModalMode('edit');
    setEditingPar(par);
    setNewPar({
      code: par.code,
      name: par.name
    });
    setShowModal(true);
  };

  const handleDeletePar = async (code) => {
    if (window.confirm(`Are you sure you want to delete Parliament constituency with code ${code}?`)) {
      try {
        const response = await parAPI.deletePar(code);
        
        if (response.success) {
          loadPars();
          console.log('PAR deleted successfully');
        } else {
          setError(response.message || 'Failed to delete PAR');
        }
      } catch (err) {
        console.error('Delete error:', err);
        setError('An error occurred while deleting the PAR');
      }
    }
  };

  const handleSavePar = async () => {
    try {
      let response;
      
      if (modalMode === 'create') {
        response = await parAPI.createPar({
          code: newPar.code,
          name: newPar.name
        });
      } else {
        response = await parAPI.updatePar(editingPar.code, {
          id: editingPar.id,
          code: newPar.code,
          name: newPar.name
        });
      }

      if (response.success) {
        setShowModal(false);
        loadPars();
        console.log(`${modalMode === 'create' ? 'Created' : 'Updated'} PAR successfully`);
      } else {
        setError(response.message || `Failed to ${modalMode} PAR`);
      }
    } catch (err) {
      console.error('Save error:', err);
      setError(`An error occurred while ${modalMode === 'create' ? 'creating' : 'updating'} the PAR`);
    }
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  if (loading && !pars.length) {
    return (
      <div className="master-data-management">
        <div className="loading">Loading PARs...</div>
      </div>
    );
  }

  return (
    <div className="master-data-management">
      <div className="master-data-management-header">
        <div className="header-info">
          <h2><i className="fas fa-university"></i> Parliament (Federal) Management</h2>
          <p>Manage Dewan Parlimen (Federal Parliament) constituencies</p>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="master-data-management-toolbar">
        <div className="search-section">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search Parliament constituencies..."
            className="search-input"
          />
          <button onClick={loadPars} className="btn-search">
            <i className="fas fa-search"></i> Search
          </button>
        </div>
        <button onClick={handleCreatePar} className="btn-create">
          <i className="fas fa-plus"></i> Add New Parliament
        </button>
      </div>

      <div className="table-container">
        <table className="master-data-table">
          <thead>
            <tr>
              <th>No.</th>
              <th>PAR Code</th>
              <th>PAR Name</th>
              <th>School Count</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pars.length > 0 ? (
              pars.map((par, index) => (
                <tr key={par.id || par.code}>
                  <td>{index + 1}</td>
                  <td><strong>{par.code}</strong></td>
                  <td>{par.name}</td>
                  <td>
                    <span className="badge badge-info">{par.schoolCount || 0} schools</span>
                  </td>
                  <td className="actions-column">
                    <button
                      onClick={() => handleEditPar(par)}
                      className="btn-action btn-edit"
                      title="Edit Parliament"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      onClick={() => handleDeletePar(par.code)}
                      className="btn-action btn-delete"
                      title="Delete Parliament"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="no-data">No Parliament constituencies found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-section">
          <div className="pagination-info">
            Page {currentPage} of {totalPages} ({totalCount} total PARs)
          </div>
          <div className="pagination-controls">
            <button
              onClick={() => goToPage(1)}
              disabled={currentPage === 1}
              className="btn-page"
            >
              First
            </button>
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="btn-page"
            >
              Previous
            </button>
            <span className="page-input">
              <input
                type="number"
                value={currentPage}
                onChange={(e) => goToPage(Number(e.target.value))}
                min="1"
                max={totalPages}
                className="page-number-input"
              />
              <span>of {totalPages}</span>
            </span>
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="btn-page"
            >
              Next
            </button>
            <button
              onClick={() => goToPage(totalPages)}
              disabled={currentPage === totalPages}
              className="btn-page"
            >
              Last
            </button>
          </div>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(parseInt(e.target.value));
              setCurrentPage(1);
            }}
            className="page-size-select"
          >
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
        </div>
      )}

      {/* Modal for Add/Edit PAR */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>
                {modalMode === 'create' ? 'Add New Parliament' : `Edit Parliament: ${newPar.code}`}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="modal-close-btn"
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="parCode">PAR Code *</label>
                <input
                  id="parCode"
                  type="text"
                  value={newPar.code}
                  onChange={(e) => setNewPar({ ...newPar, code: e.target.value.trim().toUpperCase() })}
                  placeholder="Enter Parliament code (e.g., P001, P002)"
                  maxLength={50}
                  className="form-control"
                  disabled={modalMode === 'edit'} // Don't allow changing code after creation
                />
                <small className="form-help">Unique identifier for the Parliament constituency (cannot be changed after creation)</small>
              </div>

              <div className="form-group">
                <label htmlFor="parName">PAR Name *</label>
                <input
                  id="parName"
                  type="text"
                  value={newPar.name}
                  onChange={(e) => setNewPar({ ...newPar, name: e.target.value.trim() })}
                  placeholder="Enter Parliament name (e.g., Keningau)"
                  maxLength={255}
                  className="form-control"
                />
                <small className="form-help">Full name of the Parliament constituency</small>
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePar}
                disabled={!newPar.code.trim() || !newPar.name.trim()}
                className="btn btn-primary"
              >
                {modalMode === 'create' ? 'Create Parliament' : 'Update Parliament'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParManagement;